import React, { useMemo } from 'react';
import PageNotFound from 'components/shared/Error';
import LoadingComponent from 'components/shared/Loading';
import AccessDenied from 'components/shared/AccessDenied';
import { IGenerateRoute } from 'interfaces/routesInt';
import { pageFeatures } from '../urls';
import { usePermissions } from 'hooks/usePermissions';

const GenerateRoutes = ({ splitLocation, pageRoute }: IGenerateRoute) => {
  const { hasPermission, isLoading, userData } = usePermissions();
  const segments = Array.isArray(splitLocation.segments) ? splitLocation.segments : [splitLocation.segments];

  if (isLoading) {
    return <LoadingComponent />;
  }

  // 1. Resolve the matched page and its corresponding route key
  const fullPath = segments.filter(Boolean).join('/');
  let matchedRouteKey = '';
  let page = null;

  if (pageRoute[fullPath]) {
    matchedRouteKey = fullPath;
    page = pageRoute[fullPath];
  } else {
    // Dynamic Route Matching
    const keys = Object.keys(pageRoute);
    for (const key of keys) {
      if (key.includes(':')) {
        const routeSegments = key.split('/');
        const pathSegments = segments.filter(Boolean);

        if (routeSegments.length === pathSegments.length) {
          const isMatch = routeSegments.every((segment, index) => {
            return segment.startsWith(':') || segment === pathSegments[index];
          });

          if (isMatch) {
            matchedRouteKey = key;
            page = pageRoute[key];
            break;
          }
        }
      }
    }
  }

  // Fallback to legacy first-segment matching if still no page
  if (!page) {
    const routeName = segments[1];
    if (pageRoute[routeName]) {
        matchedRouteKey = routeName;
        page = pageRoute[routeName];
    }
  }

  // 2. 🛡️ [SECURITY GATEKEEPER]
  // If we found a page, check if its route is restricted by a feature key
  if (page) {
    const matchedFeatures = pageFeatures.filter(f => {
      const urlPath = f.url.startsWith('/') ? f.url.slice(1) : f.url;
      
      // Match against the route key we identified (handles both static and dynamic)
      return urlPath === matchedRouteKey;
    });

    if (matchedFeatures.length > 0) {
      // If we don't have userData yet, we must show LoadingComponent to avoid flashing AccessDenied
      if (!userData) {
        return <LoadingComponent />;
      }

      const hasAnyPerm = matchedFeatures.some(f => hasPermission(Number(f.feature_key)));
      if (!hasAnyPerm) {
        return <AccessDenied />;
      }
    }
  }

  // 3. Final Render
  if (!page) {
    return <PageNotFound />;
  }
  return page;
};

export default GenerateRoutes;
